'use client';

import type { CSSProperties, ReactElement } from 'react';
import { Cmarkdownvisual, CmarkdownvisualType } from '../Cmarkdownvisual';
import { CONTAINER_HEIGHT } from './mindmap-constants';
import { MindmapFlowCanvas } from './MindmapFlowCanvas';
import { MindmapSideColumn } from './MindmapSideColumn';
import type { MindmapRenderModel } from './mindmap-render-model';
import { MindmapRenderKind } from './mindmap-render-model';
import {
  MINDMAP_CONTAINER_STYLE,
  MINDMAP_CSS_VALUE,
  MINDMAP_METRIC_UNIT,
} from './Cmarkdownmindmap.constants';
import type { CmarkdownmindmapParsedModel } from './useCmarkdownmindmapParsedModel';
import type { useCmarkdownmindmapController } from './useCmarkdownmindmapController';
import type { DataElScope } from '@/utils/dataEl';

type MindmapController = ReturnType<typeof useCmarkdownmindmapController>;

interface CmarkdownmindmapFlowViewProps {
  className?: string;
  controller: MindmapController;
  dataElScope: DataElScope;
  parsedModel: CmarkdownmindmapParsedModel;
  renderModel: Extract<MindmapRenderModel, { kind: MindmapRenderKind.Flow }>;
  style?: CSSProperties;
}

export function CmarkdownmindmapFlowView({
  className,
  controller,
  dataElScope,
  parsedModel,
  renderModel,
  style,
}: CmarkdownmindmapFlowViewProps): ReactElement {
  const {
    controlsScaleStyle,
    expansion,
    interactionState,
    keyboardNavigation,
    runtime,
    zoomControls,
  } = controller;

  return (
    <Cmarkdownvisual
      dataElScope={dataElScope}
      type={CmarkdownvisualType.Mindmap}
      containerStyle={MINDMAP_CONTAINER_STYLE}
      contentStyle={{
        height: CONTAINER_HEIGHT,
        minHeight: CONTAINER_HEIGHT,
        flex: 1,
      }}
      tabIndex={0}
      onKeyDown={keyboardNavigation.handleKeyDown}
      style={{ outline: MINDMAP_CSS_VALUE.None, ...style }}
      className={className}
      compactPreview={parsedModel.compactPreview}
      metric={{ value: parsedModel.nodeCount, unit: MINDMAP_METRIC_UNIT }}
    >
      <MindmapFlowCanvas
        controlsScaleStyle={controlsScaleStyle}
        dataElScope={dataElScope}
        interactionState={interactionState}
        isZoomDisabled={!runtime.reactFlowInstance}
        layout={renderModel.layout}
        onFlowInit={(instance) => {
          runtime.setReactFlowInstance(instance);
          runtime.setZoomLevel(instance.getZoom());
        }}
        onMoveZoomChange={runtime.setZoomLevel}
        onNavigate={keyboardNavigation.handleNavigate}
        onNodeMouseEnter={runtime.setHoveredNodeId}
        onNodeMouseLeave={() => runtime.setHoveredNodeId(null)}
        onZoomChange={zoomControls.handleZoomChange}
        onZoomIn={zoomControls.handleZoomIn}
        onZoomOut={zoomControls.handleZoomOut}
        reactFlowWrapperRef={runtime.reactFlowWrapperRef}
        sideColumn={
          <MindmapSideColumn
            isFocusDisabled={!runtime.reactFlowInstance}
            onCollapseAll={expansion.handleCollapseAll}
            onExpandAll={expansion.handleExpandAll}
            onFocusTree={zoomControls.handleFocusTree}
          />
        }
        zoomLevel={runtime.zoomLevel}
      />
    </Cmarkdownvisual>
  );
}
